const ONE_THOUSAND = 1000;
const ONE_MILLION = 1000000;
const BC_POSTFIX = ' до н.э.';
const CENTURY_POSTFIX = ' век';
const VV_POSTFIX = ' вв.';
const MILLION_POSTFIX = ' млн. лет';
const THOUSAND_POSTFIX = ' тыс. лет';
const MONTHS = {
  1: 'Январь',
  2: 'Февраль',
  3: 'Март',
  4: 'Апрель',
  5: 'Май',
  6: 'Июнь',
  7: 'Июль',
  8: 'Август',
  9: 'Сентябрь',
  10: 'Октябрь',
  11: 'Ноябрь',
  12: 'Декабрь',
};
const MONTHS_GENITIVE = {
  1: 'января',
  2: 'февраля',
  3: 'марта',
  4: 'апреля',
  5: 'мая',
  6: 'июня',
  7: 'июля',
  8: 'августа',
  9: 'сентября',
  10: 'октября',
  11: 'ноября',
  12: 'декабря',
};

export const useFormatEntityDate = (data) => {
  const {
    end_day,
    end_month,
    end_year,
    end_before_christ,
    start_day,
    start_month,
    start_year,
    start_before_christ,
  } = data;

  const from = ref({
    day: start_day,
    month: start_month,
    year: start_year,
    bc: start_before_christ,
  });

  const to = ref({
    day: end_day,
    month: end_month,
    year: end_year,
    bc: end_before_christ,
  });

  const fromIsSet = computed(() => {
    return dateIsSet(from.value);
  });

  const toIsSet = computed(() => {
    return dateIsSet(to.value);
  });

  const canBeCenturies = computed(() => {
    if (!onlyYears.value) return false;
    return from.value.year < 3000 && to.value.year < 3000;
  });

  const fromOnlyYear = computed(() => {
    return dateOnlyYear(from.value);
  });

  const toOnlyYear = computed(() => {
    return dateOnlyYear(to.value);
  });

  const onlyYears = computed(() => {
    return fromOnlyYear.value && toOnlyYear.value;
  });

  const beautyRange = computed(() => {
    if (!fromIsSet.value) return '';
    if (canBeCenturies.value) {
      return beautifyCenturies(from.value, to.value, BC_POSTFIX);
    }
    if (onlyYears.value) {
      return beautifyYears(from.value, to.value);
    }
    if (fromOnlyYear.value && from.value.year >= 3000) {
      const beautyYear = beautifyYear(from.value.year);
      let beautyStr = addPostfix(
        beautyYear,
        from.value.bc,
        toIsSet.value && to.value.bc,
      );

      if (toIsSet.value) {
        const toStr = addPostfix(beautyDate(to.value), to.value.bc, false);
        beautyStr += ` - ${toStr}`;
      }

      return beautyStr;
    }
    return beautifyDates(from.value, to.value, BC_POSTFIX);
  });

  const dateIsSet = (date) => {
    const { day, month, year } = date;
    return [day, month, year].some((item) => item);
  };

  const beautifyCenturies = (
    from,
    to,
    bcPostfix = BC_POSTFIX,
    singlePostfix = CENTURY_POSTFIX,
    multiPostfix = VV_POSTFIX,
  ) => {
    const { year: fromYear, bc: fromBc } = from;
    const { year: toYear, bc: toBc } = to;
    let beautyStr = `${addPostfix(
      fromYear,
      fromBc,
      toBc,
      bcPostfix,
    )} - ${addPostfix(toYear, toBc, false, bcPostfix)}`;

    const fromIsCentury = (fromYear - 1) % 100 === 0;
    const toIsCentury = toYear % 100 === 0;
    if (!fromIsCentury || !toIsCentury) return beautyStr;

    const fromRealYear = getRealYear(fromYear - 1, fromBc);
    const toRealYear = getRealYear(toYear, toBc);
    const fromCentury = fromRealYear / 100;
    const toCentury = toRealYear / 100;
    if (fromBc && fromCentury === 0) return beautyStr;
    if (toCentury === 0) return beautyStr;
    if (fromCentury === toCentury) return beautyStr;

    const centuriesCount = toCentury - fromCentury;
    if (centuriesCount === 1) {
      const str = convertToRoman(toCentury) + singlePostfix;
      return toBc ? `${str}${bcPostfix}` : str;
    }
    let fromRoman = convertToRoman(fromCentury + 1);
    if (fromBc && !toBc) fromRoman += bcPostfix;
    const toRoman = convertToRoman(toCentury);
    beautyStr = `${fromRoman} - ${toRoman} ${multiPostfix}`;
    if (toBc) beautyStr += bcPostfix;
    return beautyStr;
  };

  const addPostfix = (str, fromBc, toBc = false, bcPostfix = BC_POSTFIX) => {
    if (fromBc && !toBc) return `${str}${bcPostfix}`;
    return str;
  };

  const beautifyYears = (from, to, bcPostfix = BC_POSTFIX) => {
    const { year: fromYear, bc: fromBc } = from;
    const { year: toYear, bc: toBc } = to;

    const fromStr = addPostfix(beautifyYear(fromYear), fromBc, toBc, bcPostfix);
    const toStr = addPostfix(beautifyYear(toYear), toBc, false, bcPostfix);

    return `${fromStr} - ${toStr}`;
  };

  const beautifyDates = (from, to, bcPostfix = BC_POSTFIX) => {
    const toIsSet = dateIsSet(to);
    let dateStr = addPostfix(
      beautyDate(from),
      from.bc,
      toIsSet && to.bc,
      bcPostfix,
    );
    if (toIsSet) {
      const toStr = addPostfix(beautyDate(to), to.bc, false, bcPostfix);
      dateStr += ` - ${toStr}`;
    }
    return dateStr;
  };

  const beautifyYear = (
    year,
    millionPostfix = MILLION_POSTFIX,
    thousandPostfix = THOUSAND_POSTFIX,
  ) => {
    if (year % ONE_MILLION === 0) {
      return `${year / ONE_MILLION}${millionPostfix}`;
    }
    if (year % ONE_THOUSAND === 0) {
      return `${year / ONE_THOUSAND}${thousandPostfix}`;
    }
    return year;
  };

  const beautyDate = (date) => {
    let dateStr = '';
    const { day, month, year } = date;
    dateStr += year;
    if (day === null && month === null) {
      return dateStr;
    }
    if (day === null && month !== null) {
      return `${MONTHS[month]}, ${dateStr}`;
    }
    return `${day} ${MONTHS_GENITIVE[month]} ${dateStr}`;
  };

  const dateOnlyYear = (date) => {
    const { day, month, year } = date;
    return year !== null && [day, month].every((item) => item === null);
  };

  const getRealYear = (year, bc) => (bc ? year * -1 : year);

  const convertToRoman = (number) => {
    number = Math.abs(number);
    return [
      { value: 1000, char: 'M' },
      { value: 900, char: 'CM' },
      { value: 500, char: 'D' },
      { value: 400, char: 'CD' },
      { value: 100, char: 'C' },
      { value: 90, char: 'XC' },
      { value: 50, char: 'L' },
      { value: 40, char: 'XL' },
      { value: 10, char: 'X' },
      { value: 9, char: 'IX' },
      { value: 5, char: 'V' },
      { value: 4, char: 'IV' },
      { value: 1, char: 'I' },
    ].reduce((result, currentValue) => {
      while (number >= currentValue.value) {
        result += currentValue.char;
        number -= currentValue.value;
      }

      return result;
    }, '');
  };

  return {
    beautyRange,
  };
};
